'use client';

/**
 * Globale Error-Boundary (Next.js App Router).
 * Faengt Fehler ab, die weder route- noch layout-Boundaries schlucken.
 * Gruender 2026-08-03: „Seite darf nicht einfach so crashen".
 *
 * Fangen wir hier ab, sieht der User statt „Application error ... Digest: …"
 * eine ruhige Karte mit einem klaren „Erneut versuchen"-Button (nutzt
 * Next.js' reset() um denselben Server-Component-Baum neu zu rendern).
 */

import { useEffect } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('[global-error]', error?.message, error?.digest);
  }, [error]);

  return (
    <html lang="de">
      <body style={{
        margin: 0,
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        background: 'linear-gradient(180deg, #f7f9f6 0%, #eef4ee 100%)',
        color: '#0E1A13',
      }}>
        <div style={{
          maxWidth: 560,
          width: '100%',
          padding: '28px 28px 24px',
          background: '#fff',
          border: '1px solid rgba(10,14,26,0.08)',
          borderRadius: 18,
          boxShadow: '0 20px 60px rgba(10,14,26,0.10)',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 800, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: '#B23A3A', marginBottom: 6,
          }}>Kurze Störung</div>
          <h1 style={{
            margin: '0 0 12px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em',
          }}>
            Da ist gerade etwas schiefgelaufen.
          </h1>
          <p style={{
            margin: '0 0 20px', fontSize: 14, lineHeight: 1.55, color: '#4A5670',
          }}>
            Diese Seite konnte nicht geladen werden. Meistens hilft ein erneuter Versuch —
            oft ist unser Hintergrund-Dienst nur kurz mit einem Update beschäftigt.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => reset()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 10,
                border: '1px solid #1f7a4e',
                background: 'linear-gradient(135deg, #1f7a4e 0%, #155a39 100%)',
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Erneut versuchen
            </button>
            <a
              href="/admin"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 18px', borderRadius: 10,
                border: '1px solid rgba(10,14,26,0.14)',
                background: 'transparent', color: '#0E1A13',
                fontSize: 13, fontWeight: 600, textDecoration: 'none',
              }}
            >
              Zurück zur Übersicht
            </a>
          </div>
          {error?.digest ? (
            <p style={{
              marginTop: 18, fontSize: 11, color: '#7A8397',
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            }}>
              Ref: {error.digest}
            </p>
          ) : null}
        </div>
      </body>
    </html>
  );
}
